import { DictionaryService } from 'service/dictionary/dictionaryService';
import React from 'react';
import type { DictionaryItem, RecipeResult } from 'types/Recipes';
import {
  parseEquipments,
  parseIngredientsPrint,
  IngredientPrint as IngredientPrintType,
} from 'components/IngredientsAndEquipment/helpers/parseIngredients';
import { IngredientPrint } from './IngredientPrint';

interface IngredientsPrintProps {
  dictionary?: DictionaryItem[];
  recipe?: RecipeResult;
}

const LINE_HEIGHT = 21;
const TITLE_SPACE = 12;

const IngredientBlockPrint = ({ blockitem }: { blockitem: IngredientPrintType }) => {
  return (
    <React.Fragment>
      {!!blockitem.title && (
        <div className="ingredientText text-gray-500 relative text-sm uppercase [&:not(:first-child)]:mt-3">
          {blockitem.title}
        </div>
      )}
      {!!blockitem.summary && <IngredientPrint ingredient={blockitem} />}
    </React.Fragment>
  );
};
// column scc property does not work in safari print media, thus we need to calculate
const getFirstCol = (parsedBlock: IngredientPrintType[]) => {
  const fullHeight = parsedBlock.reduce((acc, curr) => acc + (curr.printHeight || 0), 0);
  const totalCount = parsedBlock.length;
  const halfHeight = fullHeight / 2;

  let currentHeight = 0;
  let currentIndex = 0;

  parsedBlock.forEach((block, i) => {
    currentHeight += block.printHeight || 0;
    if (currentHeight < halfHeight) {
      currentIndex = i;
    }
  });

  return totalCount < currentIndex ? totalCount : currentIndex + 2;
};

export const IngredientsPrint = ({ dictionary, recipe }: IngredientsPrintProps) => {
  const parsedBlock = parseIngredientsPrint(recipe, LINE_HEIGHT, TITLE_SPACE);

  const parsedEquipment = parseEquipments(recipe);

  const countIngredientsFirstColumn = getFirstCol(parsedBlock);
  const countEquipmentFirstColumn = Math.ceil(parsedEquipment.length / 2);

  const blockItemsFirstColumn = parsedBlock.slice(0, countIngredientsFirstColumn);
  const blockItemsSecondColumn = parsedBlock.slice(countIngredientsFirstColumn);

  return (
    <>
      {!!parsedEquipment.length && (
        <>
          <h2 className="print:uppercase text-[18px] mb-1 mt-6">
            {DictionaryService.getCopy('utensils', dictionary)}
          </h2>
          <div className="flex flex-row">
            <div className="grow min-w-[50%] max-w-[50%]">
              {parsedEquipment.map(
                (blockitem, i) =>
                  i <= countEquipmentFirstColumn && (
                    <IngredientBlockPrint blockitem={blockitem} key={i} />
                  ),
              )}
            </div>
            <div className="grow">
              {parsedEquipment.map(
                (blockitem, i) =>
                  i > countEquipmentFirstColumn && (
                    <IngredientBlockPrint blockitem={blockitem} key={i} />
                  ),
              )}
            </div>
          </div>
        </>
      )}

      <h2 className="print:uppercase text-[18px] mb-1 mt-6">
        {DictionaryService.getCopy('ingredients', dictionary)}
      </h2>

      <div className="flex flex-row">
        <div className="grow min-w-[50%] max-w-[50%]">
          {blockItemsFirstColumn.map((blockitem, i) => (
            <IngredientBlockPrint blockitem={blockitem} key={`${blockitem.title}-${i}`} />
          ))}
        </div>
        <div className="grow">
          {blockItemsSecondColumn.map((blockitem, i) => (
            <IngredientBlockPrint blockitem={blockitem} key={`${blockitem.title}-${i}`} />
          ))}
        </div>
      </div>
    </>
  );
};
